import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { navigate } from 'gatsby';
import Section from '../../../shared/Section/Section';
import { sendYandexMetrikaEvent } from '../../../helpers/yandexMetrika';
import ModalBlock from '../../../shared/ModalBlock/ModalBlock';
import ProgramRequestModal from '../../../shared/Modals/ProgramRequestModal';
import { capitalizeText, parseDate } from '../../../helpers/lib';
import {
  UpArrowIcon,
  CalendarIcon,
  BoltIcon,
  DirectionsIcon,
  TimeIcon,
} from './icons';
import styles from './styles';

const DEFAULT_PART_TIME_DATA = {
  title: 'PART TIME',
  subtitle: '(частичная занятость)',
  buttonText: 'ЗАПИСАТЬСЯ НА КУРС',
  features: [
    { icon: UpArrowIcon, text: 'Гибкость и эффективность' },
    { icon: CalendarIcon, text: '24 недели обучения (6 мес.)' },
    { icon: BoltIcon, text: 'Можно совмещать с работой' },
    { icon: DirectionsIcon, text: 'Обучение по вечерам' },
    { icon: TimeIcon, text: '25 часов в неделю' },
  ],
  badgeText: 'Удобно совмещать',
};

const DEFAULT_FULL_TIME_DATA = {
  title: 'FULL TIME',
  subtitle: '(полная занятость)',
  buttonText: 'УЗНАТЬ БОЛЬШЕ',
  features: [
    { icon: UpArrowIcon, text: 'Быстрый путь', iconProps: { isBlack: true } },
    {
      icon: CalendarIcon,
      text: '15 недель обучения (3 мес.)',
      iconProps: { isBlack: true },
    },
    {
      icon: BoltIcon,
      text: 'Невозможно совмещать с учебой/работой',
      iconProps: { isBlack: true },
    },
    {
      icon: DirectionsIcon,
      text: 'Обучение полный день',
      iconProps: { isBlack: true },
    },
    { icon: TimeIcon, text: '10 часов в день', iconProps: { isBlack: true } },
  ],
};

const FeaturesList = ({ features }) => (
  <Box sx={styles.featuresList}>
    {features.map((feature, index) => {
      const IconComponent = feature.icon;
      return (
        <Box key={index} sx={styles.featureItem}>
          <Box sx={styles.featureIcon}>
            <IconComponent {...(feature.iconProps || {})} />
          </Box>
          <Typography sx={styles.featureText}>{feature.text}</Typography>
        </Box>
      );
    })}
  </Box>
);

function FormatComparison({
  title = 'Сравните форматы',
  partTimeData,
  fullTimeData,
  ymEvents = {},
  myTargetEvents = {},
  requestLeadData,
  group,
  type,
  modalTitle,
}) {
  const isSm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [isPartTimeRequestOpen, setIsPartTimeRequestOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const formattedModalTitle =
    modalTitle || capitalizeText(Array.isArray(title) ? title.join('') : title);

  useEffect(() => {
    if (group?.startDate) {
      setStartDate(() => parseDate(group.startDate));
      setEndDate(() => parseDate(group.endDate));
    }
  }, [group]);

  const handlePartTimeClick = () => {
    sendYandexMetrikaEvent(ymEvents.partTimeClick || 'partTimeFormatClick');
    setIsPartTimeRequestOpen(true);
  };

  const handleFullTimeClick = () => {
    sendYandexMetrikaEvent(ymEvents.fullTimeClick || 'fullTimeFormatClick');
    navigate('/online-js');
  };

  const partTime = { ...DEFAULT_PART_TIME_DATA, ...partTimeData };
  const fullTime = { ...DEFAULT_FULL_TIME_DATA, ...fullTimeData };

  return (
    <Section
      title="Сравните форматы"
      withoutBg
      customStyles={{
        overflow: 'hidden',
        padding: {
          xs: '34px 0 40px 0',
          lg: '40px 0 64px 0',
        },
      }}
    >
      <Box sx={styles.container}>
        <Box sx={styles.cardWrapper}>
          <Box sx={[styles.commonCard, styles.partTimeCard]}>
            <Box sx={styles.headerWrapper}>
              <Box sx={styles.badge}>
                <Typography sx={styles.badgeText}>
                  {partTime.badgeText}
                </Typography>
              </Box>
              <Box sx={styles.titleWrapper}>
                <Typography
                  variant="desktopH2"
                  sx={[styles.title, styles.partTimeTitle]}
                >
                  {partTime.title}
                </Typography>
                <Typography sx={styles.subtitle}>
                  {partTime.subtitle}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="kitPrimary"
              size={isSm ? 'large' : 'medium'}
              onClick={handlePartTimeClick}
              sx={[styles.commonButton, styles.partTimeButton]}
            >
              {partTime.buttonText}
            </Button>
            <FeaturesList features={partTime.features} />
            <Box sx={styles.watchIcon} />
          </Box>
        </Box>
        <Box sx={styles.cardWrapper}>
          <Box sx={[styles.commonCard, styles.fullTimeCard]}>
            <Box>
              <Typography
                variant="desktopH2"
                sx={[styles.title, styles.fullTimeTitle]}
              >
                {fullTime.title}
              </Typography>
              <Typography sx={styles.subtitle}>{fullTime.subtitle}</Typography>
            </Box>
            <Button
              variant="outlined"
              size={isSm ? 'large' : 'medium'}
              onClick={handleFullTimeClick}
              sx={[styles.commonButton, styles.fullTimeButton]}
            >
              {fullTime.buttonText}
            </Button>
            <FeaturesList features={fullTime.features} />
          </Box>
        </Box>
      </Box>
      <ModalBlock
        isOpen={isPartTimeRequestOpen}
        handleClose={() => setIsPartTimeRequestOpen(false)}
      >
        <ProgramRequestModal
          extraLeadData={requestLeadData}
          startDate={startDate}
          endDate={endDate}
          date={group?.startDate}
          formData={{ title: formattedModalTitle, subtitle: type }}
          ymSubmitEventTypes={[ymEvents.signUpFormSubmit]}
          myTargetSubmitEventTypes={[myTargetEvents.signUpFormSubmit]}
        />
      </ModalBlock>
    </Section>
  );
}

export default FormatComparison;
